import { PLAN_ENTITLEMENTS, PLAN_FEATURES, PlanTier } from '@fluenceur/types';
import { FREE_PLAN_OFFER } from '@/config/free-plan-offer';
import type { PlanChoiceId } from './plan-choice-offer';

export type PlanChoiceFeature = {
  key: string;
  values?: Record<string, number>;
  /** A limit of the plan rather than something it gives. */
  locked?: boolean;
  /** One of the few lines that make a brand pick this plan, shown on its card. */
  highlight?: boolean;
};

/**
 * Everything each plan gives, in the order the full comparison lists it. The
 * card shows only the `highlight` lines, in the same order.
 */
export const PLAN_CHOICE_FEATURES: Record<PlanChoiceId, PlanChoiceFeature[]> = {
  free: [
    { key: 'pricing.plan.free.features.core' },
    { key: 'pricing.plan.free.features.programs', highlight: true },
    {
      key: 'pricing.plan.free.features.briefs',
      values: { count: PLAN_FEATURES[PlanTier.FREE].limits.maxActiveBriefs },
      highlight: true,
    },
    {
      key: 'pricing.plan.free.features.searches',
      values: { count: FREE_PLAN_OFFER.monthlyCreatorSearches },
      highlight: true,
    },
    {
      key: 'pricing.plan.free.features.profileUnlocks',
      values: { count: PLAN_ENTITLEMENTS[PlanTier.FREE].profileUnlocks!.max },
      highlight: true,
    },
    {
      key: 'pricing.plan.free.features.giftingInvitations',
      values: { count: PLAN_ENTITLEMENTS[PlanTier.FREE].giftingInvitations!.max },
      highlight: true,
    },
    { key: 'pricing.plan.free.features.noAutomation', locked: true },
    {
      key: 'pricing.plan.free.features.companion',
      values: { count: FREE_PLAN_OFFER.monthlyCompanionRecommendations },
    },
  ],
  essential: [
    { key: 'pricing.plan.essential.features.everythingFree', highlight: true },
    { key: 'pricing.plan.essential.features.programs', highlight: true },
    { key: 'pricing.plan.essential.features.unmasked', highlight: true },
    { key: 'pricing.plan.essential.features.giftingInvitations' },
    {
      key: 'pricing.plan.essential.features.searches',
      values: { count: PLAN_ENTITLEMENTS[PlanTier.ESSENTIAL].discoverySearches!.max },
    },
    { key: 'pricing.plan.essential.features.briefs' },
    {
      key: 'pricing.plan.essential.features.flows',
      values: { count: PLAN_FEATURES[PlanTier.ESSENTIAL].limits.maxFlows },
      highlight: true,
    },
    { key: 'pricing.plan.essential.features.users' },
    { key: 'pricing.plan.essential.features.coPublicity', highlight: true },
    { key: 'pricing.plan.essential.features.ai' },
    { key: 'pricing.plan.essential.features.companion' },
  ],
  growth: [
    { key: 'pricing.plan.growth.features.everythingEssential', highlight: true },
    { key: 'pricing.plan.growth.features.unlimited', highlight: true },
    { key: 'pricing.plan.growth.features.flows', highlight: true },
    { key: 'pricing.plan.growth.features.users', highlight: true },
    { key: 'pricing.plan.growth.features.searches' },
    { key: 'pricing.plan.growth.features.briefs' },
    { key: 'pricing.plan.growth.features.brands', highlight: true },
  ],
};

/** The few lines of a plan's card, never a limit. */
export const planChoiceHighlights = (plan: PlanChoiceId): PlanChoiceFeature[] =>
  PLAN_CHOICE_FEATURES[plan].filter((feature) => feature.highlight && !feature.locked);
